import { MAX_CHAPTER_CONTENT_SIZE } from "@/lib/constants";
import { jsonValuesEqual } from "@/lib/json-equality";

const PREFIX = "iloris:chapter-recovery:v1:";
const MAX_SNAPSHOTS = 20;
const MAX_TOTAL_SIZE = MAX_CHAPTER_CONTENT_SIZE * 2;
const MAX_DEPTH = 100;
const snapshots = new Map<string, string>();
let initialized = false;

function storage(): Storage | null {
    try { return typeof window === "undefined" ? null : window.sessionStorage; }
    catch { return null; }
}

function removeStored(chapterId: string) {
    try { storage()?.removeItem(PREFIX + chapterId); }
    catch { /* Memory recovery remains available when browser storage is blocked. */ }
}

function prune() {
    let total = Array.from(snapshots.values()).reduce((sum, value) => sum + value.length, 0);
    while (snapshots.size > MAX_SNAPSHOTS || total > MAX_TOTAL_SIZE) {
        const oldest = snapshots.entries().next().value;
        if (!oldest) break;
        total -= oldest[1].length;
        snapshots.delete(oldest[0]);
        removeStored(oldest[0]);
    }
}

function initialize() {
    if (initialized) return;
    initialized = true;
    try {
        const session = storage();
        if (!session) return;
        for (let index = 0; index < session.length; index += 1) {
            const key = session.key(index);
            if (!key?.startsWith(PREFIX)) continue;
            const chapterId = key.slice(PREFIX.length);
            const value = session.getItem(key);
            if (chapterId.length > 0 && chapterId.length <= 200 && value && value.length <= MAX_CHAPTER_CONTENT_SIZE) {
                snapshots.set(chapterId, value);
            }
        }
        prune();
    } catch { /* Keep any snapshots already loaded before storage became unavailable. */ }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

function boundedJson(value: unknown, depth = 0): boolean {
    if (depth > MAX_DEPTH) return false;
    if (value === null || typeof value !== "object") return true;
    return Object.values(value).every(child => boundedJson(child, depth + 1));
}

function validNode(value: unknown): boolean {
    if (!isRecord(value) || typeof value.type !== "string" || !value.type) return false;
    if (value.text !== undefined && typeof value.text !== "string") return false;
    if (value.attrs !== undefined && !isRecord(value.attrs)) return false;
    if (value.marks !== undefined && (!Array.isArray(value.marks) || !value.marks.every(mark =>
        isRecord(mark) && typeof mark.type === "string" &&
        (mark.attrs === undefined || isRecord(mark.attrs))
    ))) return false;
    return value.content === undefined || (Array.isArray(value.content) && value.content.every(validNode));
}

function parseDocument(serialized: string | undefined | null): Record<string, unknown> | null {
    if (!serialized || serialized.length > MAX_CHAPTER_CONTENT_SIZE) return null;
    try {
        const value: unknown = JSON.parse(serialized);
        return isRecord(value) && value.type === "doc" && boundedJson(value) && validNode(value) ? value : null;
    } catch { return null; }
}

function serializeDocument(content: unknown): string | null {
    try {
        const serialized = JSON.stringify(content);
        return parseDocument(serialized) ? serialized : null;
    } catch { return null; }
}

function matches(serialized: string | null | undefined, content: unknown): boolean {
    const stored = parseDocument(serialized);
    const candidate = serializeDocument(content);
    return Boolean(stored && candidate && jsonValuesEqual(stored, JSON.parse(candidate)));
}

/** Returns a separate snapshot for an explicit Restore/Discard prompt. */
export function readChapterRecovery(chapterId: string, serverContent: unknown): Record<string, unknown> | null {
    initialize();
    const serialized = snapshots.get(chapterId);
    const content = parseDocument(serialized);
    if (!content) {
        snapshots.delete(chapterId);
        removeStored(chapterId);
        return null;
    }
    if (matches(serialized, serverContent)) {
        clearChapterRecovery(chapterId, content);
        return null;
    }
    return content;
}

/** True means durable for this tab's reload; false retains a bounded memory fallback. */
export function writeChapterRecovery(chapterId: string, content: Record<string, unknown>): boolean {
    initialize();
    if (!chapterId || chapterId.length > 200) return false;
    const serialized = serializeDocument(content);
    if (!serialized) return false;
    snapshots.delete(chapterId);
    snapshots.set(chapterId, serialized);
    prune();
    try {
        const session = storage();
        if (!session) return false;
        // Reinsert to preserve oldest-to-newest eviction order after a reload.
        session.removeItem(PREFIX + chapterId);
        session.setItem(PREFIX + chapterId, serialized);
        return true;
    } catch { return false; }
}

/** A late successful save/discard may only remove the snapshot it actually saved. */
export function clearChapterRecovery(chapterId: string, savedContent: unknown): void {
    initialize();
    if (matches(snapshots.get(chapterId), savedContent)) snapshots.delete(chapterId);
    try {
        const session = storage();
        if (session && matches(session.getItem(PREFIX + chapterId), savedContent)) {
            session.removeItem(PREFIX + chapterId);
        }
    } catch { /* Never lose the in-memory fallback because browser storage failed. */ }
}
